import { Component, OnInit, OnDestroy } from "@angular/core";
import { Route, Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { AuthService } from "../../services/auth.service";
import { RoutesService } from "../../services/routes.service";
import { ConfigurationService } from "../../services/configuration.service";
import { AuthenticationMode } from "../../enums/authenticationMode";
import { IBusinessFunction } from "../../interfaces/ibusinessfunction";
import { BusinessFunctionService } from "../../services/businessfunction.service";

@Component({
    selector: "nav-menu",
    templateUrl: "./navmenu.component.html",
    standalone: false
})

export class NavMenuComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    businessFunctionDisplayName = "";
    businessFunctionUrl = "";
    hideMenu = true;
    routes: any[] = new Array();
    homeRouteLink = "";
    loggedIn = false;
    usersName = "";
    multiplePortals = false;
    businessFunction!: IBusinessFunction;
    beSpokeLogo: any;

    constructor(private router: Router,
        private routesService: RoutesService,
        private authService: AuthService,
        private toastr: ToastrService,
        private configurationService: ConfigurationService,
        private businessFunctionService: BusinessFunctionService) {

    }

    ngOnInit(): void {
        this.buildMenu();
        this.authService.businessFunctionAnnounced$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.buildMenu();
            });

        this.authService.userLogOutAnnouced$
            .pipe(takeUntil(this.destroy$))
            .subscribe((userLoggedOut: boolean) => {
                if (userLoggedOut) {
                    // needed to ensure the user is directed to the right page
                    // based on whether authentication is required or not
                    const bf: any = AuthService.getBusinessFunctionByDefaultRoute(this.businessFunctionUrl);
                    if (bf) {
                        if (bf.authenticationRequired) {
                            this.configurationService.getAuthenticationMode()
                                .subscribe((mode: number) => {
                                if (mode === AuthenticationMode.DPASS) {
                                    window.location.assign(`${bf.defaultRoute}/signoutuser`);
                                } else {
                                    if (AuthService.manualLogin) {
                                        this.router.navigate([`${bf.defaultRoute}/signedout`]);
                                    } else {
                                        window.location.href =
                                            `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${window.location.origin}${bf.defaultRoute}/signedout`;
                                    }
                                }
                            });
                        } else {
                            this.router.navigate([`${bf.defaultRoute}`]);
                        }
                    } else {
                        this.router.navigate(["/splash"]);
                    }
                }
            });
    }

    /**
     * Builds the navigation menu. Put in to a separate method so it can be reused
     * when certian events occur
     */
    buildMenu(): void {
        if (AuthService.businessFunctionId && AuthService.businessFunction) {
            this.businessFunctionDisplayName = AuthService.businessFunction.displayName;
            this.businessFunctionUrl = AuthService.businessFunction.defaultRoute;

            this.businessFunctionService.getImage(AuthService.businessFunctionId)
                .pipe(takeUntil(this.destroy$))
                .subscribe((image: String) => {
                    if (image == undefined || image == null || image == "") {
                        this.beSpokeLogo = "/images/deloitte.svg";
                    }
                    else {
                        this.beSpokeLogo = image;
                    }
                });

            // set routes
            this.routes = this.routesService.getNavigationRoutes(this.router, AuthService.businessFunction);
            this.routes.forEach((r: Route) => {
                if (r.data && r.path && AuthService.businessFunction) {
                    r.data['nav'].navPath = r.path.replace(":defaultroute", AuthService.businessFunction.defaultRoute);
                }
            });

            const homeRoute = this.routes.find((r: Route) => r.data && r.data['nav'].text === "Home");
            this.homeRouteLink = homeRoute?.data?.['nav']?.navPath || "";

            if (AuthService.user && AuthService.businessFunction?.authenticationRequired) {
                this.loggedIn = true;
                this.usersName = `${AuthService.user.firstName} ${AuthService.user.lastName}`;
                if (AuthService.user.businessFunctions.length > 1) {
                    this.multiplePortals = true;
                }
            } else {
                this.loggedIn = false;
                this.usersName = "";
            }
        }
    }

    toggleMenu(): void {
        this.hideMenu ? this.hideMenu = false : this.hideMenu = true;
    }

    closeMenu(): void {
        this.hideMenu = true;
    }

    signOutClicked(): void {
        this.authService.logOut = true;
        this.toastr.success("Logged out successfully");
    }

    /**
     * The reason this is used is because the router by default will match all routes and set them as active even if they do not 100% match
     * As we do not set the home page in the url for each business function, this is assumed as always being selected. So this check prevents
     * the home page link from being set to selected if one of the other links is clicked.
     *
     * Without this check, and having exact to false when navigating to any page that has a parameter, the selected navigation will not work.
     * We need to come back and make this more elegant
     * @param name - This is the text for the url. It is needed to prevent the Home route from always being selected as all other routes start
     * with /businessfunctionid
     */
    isTrue(name: string): boolean {
        return name === "Home";
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}